import { NavigateOptions } from '@reach/router';
import navigate from '../../routing/navigate';
import { transition } from './machine/machine';
import { IWizardFlowContext, WizardFlowAction, WizardFlowState } from './types';
import wizardFlowChart from './wizardFlowChart';
import wizardFlowRoutesMap from './wizardFlowRoutesMap';

interface IWizardFlowController {
  context: IWizardFlowContext;
  nextState: WizardFlowState;
  startSession(): void;
  goTo(state: WizardFlowState, options?: NavigateOptions<void>): void;
  goNext(options?: NavigateOptions<void>): WizardFlowState;
  goBack(options?: NavigateOptions<void>): WizardFlowState;
  goEdit(options?: NavigateOptions<void>): WizardFlowState;
}

function navigateToState(
  state: WizardFlowState,
  options: NavigateOptions<void>,
) {
  if (!wizardFlowRoutesMap.has(state)) {
    throw new Error(`Route path not defined for state ${state}`);
  }

  navigate(wizardFlowRoutesMap.get(state), options);
}

function createWizardFlowController(
  currentState: WizardFlowState,
  context: IWizardFlowContext,
): IWizardFlowController {
  function wizardTransition(action: WizardFlowAction) {
    return transition(wizardFlowChart, currentState, action, context);
  }

  function applyTransition(
    action: WizardFlowAction,
    options: NavigateOptions<void>,
  ) {
    const next = wizardTransition(action);
    if (!next) {
      throw new Error(`"${currentState}" has not action "${action}"`);
    }

    if (typeof next.fn === 'function') {
      next.fn();
    }

    navigateToState(next.state, options);
    return next.state;
  }

  return {
    context,
    get nextState() {
      const next = wizardTransition(WizardFlowAction.NEXT);
      return next && next.state;
    },
    startSession: context.startWizardSession,
    goTo: navigateToState,
    goNext: (options) => applyTransition(WizardFlowAction.NEXT, options),
    goBack: (options) => applyTransition(WizardFlowAction.BACK, options),
    goEdit: (options) => applyTransition(WizardFlowAction.EDIT, options),
  };
}

export { createWizardFlowController, IWizardFlowController };
